import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface CustomInvoiceItem {
  id?: number;
  custom_title: string;
  billing_type: string;
  rate: number;
  quantity: number;
  description?: string;
  amount?: number;
  is_custom?: boolean;
}

interface CustomInvoiceItemFormProps {
  invoiceId: number;
  invoiceStatus: string;
  editingItem?: CustomInvoiceItem | null;
  onItemAdded?: (item: CustomInvoiceItem) => void;
  onItemUpdated?: (item: CustomInvoiceItem) => void;
  onCancel?: () => void;
  isPmpAdmin?: boolean;
  isInvoiceCreator?: boolean;
}

const CustomInvoiceItemForm: React.FC<CustomInvoiceItemFormProps> = ({
  invoiceId,
  invoiceStatus,
  editingItem,
  onItemAdded,
  onItemUpdated,
  onCancel,
  isPmpAdmin = false,
  isInvoiceCreator = false,
}) => {
  const [formData, setFormData] = useState<CustomInvoiceItem>({
    custom_title: '',
    billing_type: 'page_based',
    rate: 0,
    quantity: 1,
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = isPmpAdmin || isInvoiceCreator;
  const isDraft = invoiceStatus === 'draft';
  const isEditing = !!editingItem;

  // Initialize form data when editing
  useEffect(() => {
    if (editingItem && isEditing) {
      setFormData({
        custom_title: editingItem.custom_title,
        billing_type: editingItem.billing_type,
        rate: editingItem.rate,
        quantity: editingItem.quantity,
        description: editingItem.description || '',
      });
    } else if (!isEditing) {
      // Reset to empty form when not editing
      setFormData({
        custom_title: '',
        billing_type: 'page_based',
        rate: 0,
        quantity: 1,
        description: '',
      });
    }
  }, [editingItem, isEditing]);

  const handleInputChange = (field: keyof CustomInvoiceItem, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate amount when rate or quantity changes
    if (field === 'rate' || field === 'quantity') {
      const newRate = field === 'rate' ? Number(value) : formData.rate;
      const newQuantity = field === 'quantity' ? Number(value) : formData.quantity;
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        amount: newRate * newQuantity 
      }));
    }
  };

  const resetForm = () => {
    if (isEditing && editingItem) {
      // Reset to editing item data
      setFormData({
        custom_title: editingItem.custom_title,
        billing_type: editingItem.billing_type,
        rate: editingItem.rate,
        quantity: editingItem.quantity,
        description: editingItem.description || '',
      });
    } else {
      // Reset to empty form
      setFormData({
        custom_title: '',
        billing_type: 'page_based',
        rate: 0,
        quantity: 1,
        description: '',
      });
    }
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canEdit || !isDraft) {
      setError('You cannot edit this invoice');
      return;
    }

    if (!formData.custom_title.trim()) {
      setError('Item title is required');
      return;
    }

    if (formData.rate <= 0) {
      setError('Rate must be greater than 0');
      return;
    }

    if (formData.quantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing && editingItem?.id) {
        // Update existing item
        const response = await api.put(
          `/vendor-invoices/${invoiceId}/custom-items/${editingItem.id}`,
          formData
        );
        
        if (response.data.success) {
          onItemUpdated?.(response.data.data);
          resetForm();
        } else {
          setError(response.data.message || 'Failed to update item');
        }
      } else {
        // Add new item
        const response = await api.post(
          `/vendor-invoices/${invoiceId}/custom-items`,
          formData
        );
        
        if (response.data.success) {
          onItemAdded?.(response.data.data);
          resetForm();
        } else {
          setError(response.data.message || 'Failed to add item');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save custom item');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      // Reset to editing item data when canceling edit
      setFormData({
        custom_title: editingItem?.custom_title || '',
        billing_type: editingItem?.billing_type || 'page_based',
        rate: editingItem?.rate || 0,
        quantity: editingItem?.quantity || 1,
        description: editingItem?.description || '',
      });
    } else {
      // Reset to empty form when canceling add
      resetForm();
    }
    onCancel?.();
  };

  // Auto-calculate amount when form data changes
  useEffect(() => {
    const amount = formData.rate * formData.quantity;
    setFormData(prev => ({ ...prev, amount }));
  }, [formData.rate, formData.quantity]);

  if (!canEdit) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edit Custom Invoice Item' : 'Add Custom Invoice Item'}
      </Typography>
      
      {!isDraft && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Custom items can only be added to draft invoices
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}



      {isDraft && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <TextField
                fullWidth
                required
                label="Item Title"
                value={formData.custom_title}
                onChange={(e) => handleInputChange('custom_title', e.target.value)}
                placeholder="e.g., Document Review, Research, etc."
              />
            </Box>
            
            <Box>
              <FormControl fullWidth required>
                <InputLabel>Billing Type</InputLabel>
                <Select
                  value={formData.billing_type}
                  onChange={(e) => handleInputChange('billing_type', e.target.value)}
                  label="Billing Type"
                >
                  <MenuItem value="page_based">Page Based</MenuItem>
                  <MenuItem value="case_based">Case Based</MenuItem>
                  <MenuItem value="time_based">Time Based</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <TextField
                fullWidth
                required
                label="Rate per unit"
                type="number"
                value={formData.rate}
                onChange={(e) => handleInputChange('rate', Number(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0.00"
              />
            </Box>
            
            <Box>
              <TextField
                fullWidth
                required
                label="Quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', Number(e.target.value))}
                inputProps={{ min: 1 }}
                placeholder="1"
              />
            </Box>
            
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Description (Optional)"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Additional details about this item..."
              />
            </Box>
            
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount: {formData.amount ? `$${(Number(formData.amount) || 0).toFixed(2)}` : '$0.00'}
                </Typography>
                
                <Box sx={{ ml: 'auto' }}>
                  {isEditing ? (
                    <>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        Update Item
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<AddIcon />}
                      disabled={loading}
                    >
                      Add Custom Item
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomInvoiceItemForm;
